import { Dispatch, SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, SelectChangeEvent } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { EditOutlined, MoreVert, People } from '@mui/icons-material';
import {
  ActionMenu,
  ActionsType,
  Pagination,
  PaginationAPI,
  RowsPerPageSelector,
  usePartner,
} from '@shared';
import { ManageUserAPI, Roles } from 'App';

import { RoleBox } from './RoleBox';

type TypeProps = {
  data: ManageUserAPI[];
  pagination: PaginationAPI;
  rowsPerPage: number;
  loading: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  dispatch: (action: ActionsType) => void;
  handleRemoveUser: (userId: ManageUserAPI) => void;
  handleChangeRoles: (user: ManageUserAPI, roles: Roles[]) => void;
  onOpen: (user: ManageUserAPI) => void;
  partnerId: string;
};

export const UsersTable = ({
  data,
  loading,
  pagination,
  rowsPerPage,
  page,
  setPage,
  dispatch,
  handleRemoveUser,
  handleChangeRoles,
  onOpen,
  partnerId,
}: TypeProps) => {
  const [, setSearchParams] = useSearchParams();
  const { enableSelfService } = usePartner({ partner: partnerId });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    const newLimit = event.target.value;
    dispatch && dispatch({ type: 'rowsPerPage', payload: Number(newLimit) });
    setPage(1);
    setSearchParams(() => ({
      limit: String(newLimit),
    }));
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'First name',
      width: 200,
      editable: false,
      sortable: false,
      hideable: true,
    },
    {
      field: 'lastName',
      headerName: 'Last name',
      width: 200,
      sortable: false,
      editable: false,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 350,
      editable: false,
      sortable: false,
    },
    {
      field: 'roles',
      headerName: 'Roles',
      sortable: false,
      editable: false,
      width: 350,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {row.roles.map((label: Roles) => (
            <RoleBox
              key={label}
              label={label}
              onDelete={
                enableSelfService && row.roles.length > 1
                  ? () => handleChangeRoles(row, [label])
                  : undefined
              }
            />
          ))}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      editable: false,
      align: 'right',
      width: 20,
      renderCell: ({ row }) =>
        enableSelfService && (
          <ActionMenu
            icon={MoreVert}
            items={[
              {
                label: 'Remove User',
                icon: EditOutlined,
                onClick: () => handleRemoveUser(row),
              },
              {
                label: 'Change role',
                icon: People,
                onClick: () => onOpen(row),
              },
            ]}
          />
        ),
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        autoHeight
        getRowId={(row) => row.uuid}
        columns={columns}
        rows={data}
        loading={loading}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableRowSelectionOnClick
        hideFooter={pagination.totalCount <= 20}
        sx={{ '--DataGrid-overlayHeight': '52px' }}
        slots={{
          pagination: () => (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: pagination.totalPages <= 1 ? 'flex-end' : 'center',
                gap: 2,
                px: 1,
              }}
            >
              {pagination.totalPages > 1 && (
                <Pagination
                  totalPages={pagination.totalPages}
                  onPageChange={setPage}
                  currentPage={page}
                />
              )}
              <RowsPerPageSelector
                entityName={'row'}
                options={[20, 50, 100]}
                value={rowsPerPage}
                handleChange={handleChangeRowsPerPage}
              />
            </Box>
          ),
          noRowsOverlay: () => <Box sx={{ width: '100%', py: 2, px: 1 }}>No users found</Box>,
        }}
      />
    </Box>
  );
};
